// Запись голосового: MediaRecorder + громкость с микрофона для волны.

export interface VoiceRecording {
  blob: Blob;
  mime: string;
  durationMs: number;
  /** Столбики волны 0–100. */
  waveform: number[];
}

export const VOICE_MAX_MS = 5 * 60_000;
/** Короче — случайное нажатие, не отправляем. */
export const VOICE_MIN_MS = 700;
export const WAVEFORM_BARS = 48;

// AAC в MP4 играет везде — просим его первым (Safari, свежий Chrome). Иначе
// Opus в WebM/Ogg: сервер перекодирует его в AAC.
const MIME_PREFS = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

export function voiceSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof window.MediaRecorder === 'function' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

function pickMime(): string | undefined {
  if (typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  return MIME_PREFS.find((t) => MediaRecorder.isTypeSupported(t));
}

/** Уровни громкости → bars столбиков 0–100 (пик в каждом окне). */
export function downsample(levels: number[], bars: number): number[] {
  if (levels.length === 0) return [];
  const out: number[] = [];
  for (let i = 0; i < bars; i += 1) {
    const from = Math.floor((i * levels.length) / bars);
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / bars));
    let peak = 0;
    for (let j = from; j < to && j < levels.length; j += 1) peak = Math.max(peak, levels[j]!);
    out.push(Math.round(peak * 100));
  }
  return out;
}

export class VoiceRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private ctx: AudioContext | null = null;
  private timer = 0;
  private levels: number[] = [];
  private stopped: Promise<void> | null = null;
  /** Громкость 0–1 десять раз в секунду — для живой волны в поле ввода. */
  onLevel: ((level: number) => void) | null = null;

  /** Звать прямо из обработчика нажатия: iOS запускает AudioContext только по жесту. */
  async start(): Promise<void> {
    const AC =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    try {
      this.ctx = AC ? new AC() : null;
    } catch {
      this.ctx = null;
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      this.cleanup();
      throw err;
    }
    const mime = pickMime();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(this.stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: 48_000 });
    } catch {
      recorder = new MediaRecorder(this.stream);
    }
    this.recorder = recorder;
    this.chunks = [];
    this.levels = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.stopped = new Promise((resolve) => {
      recorder.onstop = () => resolve();
    });
    // Без timeslice: Safari отдаёт один цельный MP4, кусочки склеиваются плохо.
    recorder.start();
    this.startedAt = performance.now();
    this.watchLevel();
  }

  get elapsedMs(): number {
    return this.startedAt ? performance.now() - this.startedAt : 0;
  }

  private watchLevel(): void {
    if (!this.ctx || !this.stream) return;
    try {
      void this.ctx.resume?.();
      const source = this.ctx.createMediaStreamSource(this.stream);
      const analyser = this.ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      this.timer = window.setInterval(() => {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) sum += buf[i]! * buf[i]!;
        // Речь в RMS — примерно 0.01–0.3: корень растягивает тихое, волна не плоская.
        const level = Math.min(1, Math.sqrt(Math.sqrt(sum / buf.length)) * 1.6);
        this.levels.push(level);
        this.onLevel?.(level);
      }, 100);
    } catch {
      /* без волны запись всё равно идёт */
    }
  }

  async stop(): Promise<VoiceRecording> {
    const recorder = this.recorder;
    if (!recorder) throw new Error('Запись не начата');
    const durationMs = Math.round(this.elapsedMs);
    if (recorder.state !== 'inactive') recorder.stop();
    await this.stopped;
    const mime = recorder.mimeType || this.chunks[0]?.type || 'audio/webm';
    const blob = new Blob(this.chunks, { type: mime });
    const waveform = downsample(this.levels, WAVEFORM_BARS);
    this.cleanup();
    return { blob, mime, durationMs, waveform };
  }

  cancel(): void {
    try {
      if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    } catch {
      /* уже остановлен */
    }
    this.cleanup();
  }

  private cleanup(): void {
    window.clearInterval(this.timer);
    this.timer = 0;
    // Микрофон отпускаем сразу: пока он занят, iOS играет звук тихо, в разговорный динамик.
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.onLevel = null;
    this.recorder = null;
    this.startedAt = 0;
  }
}

/** «audio/webm;codecs=opus» → расширение для имени файла. */
export function voiceFileName(mime: string): string {
  const base = mime.split(';')[0]?.trim() ?? '';
  if (base === 'audio/mp4') return 'voice.m4a';
  if (base === 'audio/ogg') return 'voice.ogg';
  return 'voice.webm';
}
